<template>
  <div class="create-withdrawal-view">
    <div class="page-header">
      <div class="header-left">
        <button class="back-btn" @click="handleCancel">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div class="header-info">
          <h1 class="page-heading">Nouveau retrait de stock</h1>
          <p class="page-subtitle">Enregistrez un retrait de stock et ajustez votre inventaire</p>
        </div>
      </div>
      <div class="actions">
        <button class="secondary-btn" @click="handleCancel">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
          Annuler
        </button>
        <button class="primary-btn" :disabled="isSubmitting || items.length === 0" @click="handleSubmit">
          <span v-if="isSubmitting" class="spinner-small"></span>
          <template v-else>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Enregistrer
          </template>
        </button>
      </div>
    </div>

    <div class="layout">
      <div class="left-column">
        <div class="progress-indicator">
          <div class="progress-step" :class="{ active: true, completed: form.withdrawal_date && form.reason }">
            <div class="step-number">1</div>
            <div class="step-info">
              <span class="step-title">Détails</span>
              <span class="step-desc">Informations du retrait</span>
            </div>
          </div>
          <div class="progress-line" :class="{ completed: form.withdrawal_date && form.reason }"></div>
          <div class="progress-step" :class="{ active: form.withdrawal_date && form.reason, completed: items.length > 0 }">
            <div class="step-number">2</div>
            <div class="step-info">
              <span class="step-title">Produits</span>
              <span class="step-desc">Ajoutez les articles</span>
            </div>
          </div>
          <div class="progress-line" :class="{ completed: items.length > 0 }"></div>
          <div class="progress-step" :class="{ active: items.length > 0 }">
            <div class="step-number">3</div>
            <div class="step-info">
              <span class="step-title">Validation</span>
              <span class="step-desc">Finalisez le retrait</span>
            </div>
          </div>
        </div>

        <div class="card details-card">
          <div class="card-header">
            <div class="card-icon details-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
              </svg>
            </div>
            <div class="card-title-group">
              <h2 class="card-title">Informations du retrait</h2>
              <p class="card-subtitle">Définissez les détails du retrait de stock</p>
            </div>
          </div>

          <div class="form-grid">
            <div class="form-group">
              <label>
                Date de retrait
                <span class="required">*</span>
              </label>
              <div class="input-with-icon">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <input type="date" v-model="form.withdrawal_date" />
              </div>
              <span v-if="errors.withdrawal_date" class="field-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {{ errors.withdrawal_date }}
              </span>
            </div>

            <div class="form-group">
              <label>
                Raison du retrait
                <span class="required">*</span>
              </label>
              <div class="input-with-icon">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <select v-model="form.reason">
                  <option value="">Sélectionnez une raison</option>
                  <option value="damage">Dommage/Casse</option>
                  <option value="loss">Perte</option>
                  <option value="theft">Vol</option>
                  <option value="expired">Expiré</option>
                  <option value="return_to_supplier">Retour fournisseur</option>
                  <option value="adjustment">Ajustement</option>
                  <option value="other">Autre</option>
                </select>
              </div>
              <span v-if="errors.reason" class="field-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {{ errors.reason }}
              </span>
            </div>

            <div class="form-group full-width">
              <label>Remarques</label>
              <div class="textarea-wrapper">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                <textarea v-model="form.notes" rows="3" placeholder="Notes additionnelles pour ce retrait..."></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="right-column">
        <div class="card summary-card sticky-card">
          <div class="summary-header">
            <div class="summary-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M9 11l3 3L22 4"></path>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
              </svg>
            </div>
            <h3>Résumé</h3>
          </div>

          <div class="summary-content">
            <div class="summary-item">
              <div class="summary-label">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                  <line x1="7" y1="7" x2="7.01" y2="7"></line>
                </svg>
                Nombre d'articles
              </div>
              <div class="summary-value">{{ items.length }}</div>
            </div>

            <div class="summary-divider"></div>

            <div class="summary-item">
              <div class="summary-label">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"></path>
                  <path d="m3.3 7 8.7 5 8.7-5"></path>
                  <path d="M12 22V12"></path>
                </svg>
                Quantité totale
              </div>
              <div class="summary-value highlight">{{ totalQuantity }} unité(s)</div>
            </div>
          </div>

          <div class="summary-actions">
            <button class="summary-save-btn" :disabled="isSubmitting || items.length === 0" @click="handleSubmit">
              <span v-if="isSubmitting" class="spinner-small"></span>
              <template v-else>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                Enregistrer le retrait
              </template>
            </button>
          </div>
        </div>
      </div>
    </div>

    <br>
    <div class="card products-card">
      <div class="card-header">
        <div class="card-icon products-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
            <line x1="7" y1="7" x2="7.01" y2="7"></line>
          </svg>
        </div>
        <div class="card-title-group">
          <h2 class="card-title">Articles à retirer</h2>
          <p class="card-subtitle">{{ items.length }} article{{ items.length > 1 ? 's' : '' }}</p>
        </div>
      </div>

      <div class="search-toolbar">
        <div class="search-box">
          <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input type="text" v-model="globalSearch" placeholder="Rechercher un produit (avec suivi de stock)..."
            @input="onGlobalSearch" />
          <div v-if="globalSuggestions.length > 0" class="quick-add-badge">
            {{ globalSuggestions.length }} trouvé{{ globalSuggestions.length > 1 ? 's' : '' }}
          </div>
        </div>
        <button class="add-quick-btn" @click="addLineFromGlobal" :disabled="!canAddFromGlobal">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Ajouter
        </button>
      </div>

      <div v-if="items.length === 0" class="empty-state">
        <div class="empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"></path>
            <path d="m3.3 7 8.7 5 8.7-5"></path>
            <path d="M12 22V12"></path>
          </svg>
        </div>
        <h3>Aucun article ajouté</h3>
        <p>Commencez par rechercher et ajouter des produits à retirer</p>
        <button class="add-first-line-btn" @click="addEmptyLine">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Ajouter le premier article
        </button>
      </div>

      <div v-else class="table-container">
        <div class="table-wrapper" style="min-height: 500px;">
          <table class="products-table">
            <thead>
              <tr>
                <th class="col-product">Produit</th>
                <th class="col-number">Stock disponible</th>
                <th class="col-number">Quantité à retirer</th>
                <th class="col-number">Stock après retrait</th>
                <th class="col-notes">Notes</th>
                <th class="col-action"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(line, index) in items" :key="index" class="product-row" :class="{ 'has-error': line.hasError }">
                <td class="col-product">
                  <div class="product-cell">
                    <div class="product-search-wrapper">
                      <svg class="search-icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      <input type="text" v-model="line.search"
                        :placeholder="line.product ? line.product.name : 'Rechercher un produit...'" 
                        @input="searchProducts(index)"
                        @focus="line.showDropdown = true" 
                        class="product-input" />
                    </div>
                    <transition name="dropdown">
                      <div v-if="line.showDropdown && line.suggestions.length" class="product-dropdown">
                        <div v-for="p in line.suggestions" :key="p.id" class="product-option"
                          @click="selectProduct(index, p)">
                          <div class="product-option-content">
                            <div class="product-name">{{ p.name }}</div>
                            <div class="product-meta">
                              <span class="product-id">#{{ p.id }}</span>
                              <span class="product-stock">Stock: {{ p.stock_quantity || 0 }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </transition>
                    <div v-if="line.error" class="field-error-inline">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                      {{ line.error }}
                    </div>
                  </div>
                </td>
                <td class="col-number">
                  <div class="stock-display">
                    <span v-if="line.product" class="stock-value" :class="getStockClass(line.product.stock_quantity)">
                      {{ line.product.stock_quantity || 0 }}
                    </span>
                    <span v-else class="stock-placeholder">-</span>
                  </div>
                </td>
                <td class="col-number">
                  <div class="number-input-wrapper">
                    <input 
                      type="number" 
                      min="0.01" 
                      step="0.01" 
                      v-model.number="line.quantity"
                      @input="validateQuantity(index)" 
                      class="number-input" 
                      :class="{ 'input-error': line.quantityError }"
                      :max="line.product ? line.product.stock_quantity : undefined"
                    />
                  </div>
                  <div v-if="line.quantityError" class="field-error-inline small">
                    {{ line.quantityError }}
                  </div>
                </td>
                <td class="col-number">
                  <div class="stock-after-display">
                    <span v-if="line.product && line.quantity" class="stock-value" :class="getStockClass(calculateStockAfter(line))">
                      {{ calculateStockAfter(line) }}
                    </span>
                    <span v-else class="stock-placeholder">-</span>
                  </div>
                </td>
                <td class="col-notes">
                  <input 
                    type="text" 
                    v-model="line.notes" 
                    placeholder="Notes optionnelles..." 
                    class="notes-input"
                  />
                </td>
                <td class="col-action">
                  <button class="delete-btn" @click="confirmRemoveLine(index)" title="Supprimer">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="add-line-section">
          <button class="add-line-btn" @click="addEmptyLine">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Ajouter une ligne
          </button>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <transition name="modal">
      <div v-if="showCancelModal" class="modal-overlay" @click="showCancelModal = false">
        <div class="modal-content" @click.stop>
          <div class="modal-header warning">
            <div class="modal-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </div>
            <h3>Annuler le retrait ?</h3>
          </div>
          <div class="modal-body">
            <p>Toutes les informations saisies seront perdues. Cette action est irréversible.</p>
          </div>
          <div class="modal-actions">
            <button class="modal-btn secondary" @click="showCancelModal = false">
              Continuer l'édition
            </button>
            <button class="modal-btn danger" @click="confirmCancel">
              Oui, annuler
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal">
      <div v-if="showDeleteModal" class="modal-overlay" @click="showDeleteModal = false">
        <div class="modal-content" @click.stop>
          <div class="modal-header danger">
            <div class="modal-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </div>
            <h3>Supprimer cet article ?</h3>
          </div>
          <div class="modal-body">
            <p>Voulez-vous vraiment supprimer cet article du retrait ?</p>
          </div>
          <div class="modal-actions">
            <button class="modal-btn secondary" @click="showDeleteModal = false">
              Annuler
            </button>
            <button class="modal-btn danger" @click="confirmDelete">
              Supprimer
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal">
      <div v-if="showSubmitModal" class="modal-overlay" @click="showSubmitModal = false">
        <div class="modal-content" @click.stop>
          <div class="modal-header success">
            <div class="modal-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <h3>Enregistrer ce retrait ?</h3>
          </div>
          <div class="modal-body">
            <div class="modal-summary">
              <div class="modal-summary-item">
                <span class="label">Date:</span>
                <span class="value">{{ formatDate(form.withdrawal_date) }}</span>
              </div>
              <div class="modal-summary-item">
                <span class="label">Raison:</span>
                <span class="value">{{ getReasonLabel(form.reason) }}</span>
              </div>
              <div class="modal-summary-item">
                <span class="label">Articles:</span>
                <span class="value">{{ items.length }}</span>
              </div>
              <div class="modal-summary-item">
                <span class="label">Quantité totale:</span>
                <span class="value highlight">{{ totalQuantity }} unité(s)</span>
              </div>
            </div>
          </div>
          <div class="modal-actions">
            <button class="modal-btn secondary" @click="showSubmitModal = false">
              Annuler
            </button>
            <button class="modal-btn primary" @click="confirmSubmit" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="spinner-small"></span>
              <span v-else>Confirmer</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import { productsService } from '@/services'
import stockWithdrawalService from '@/services/stockWithdrawalService'
import { useToastStore } from '@/store/modules/toastStore'
import { useRouter } from 'vue-router'

export default defineComponent({
  name: 'CreateStockWithdrawalView',
  setup() {
    const router = useRouter()
    const toastStore = useToastStore()

    const isSubmitting = ref(false)
    const errors = ref({})
    const form = ref({
      withdrawal_date: new Date().toISOString().slice(0, 10),
      reason: '',
      notes: ''
    })

    const items = ref([])
    const globalSearch = ref('')
    const globalSuggestions = ref([])
    const globalSearchTimer = ref(null)

    const showCancelModal = ref(false)
    const showDeleteModal = ref(false)
    const showSubmitModal = ref(false)
    const lineToDelete = ref(null)

    const canAddFromGlobal = computed(() => globalSuggestions.value.length > 0)

    const totalQuantity = computed(() => {
      return items.value.reduce((sum, line) => {
        const qty = Number(line.quantity) || 0
        return sum + qty
      }, 0)
    })

    const addEmptyLine = () => {
      items.value.push({
        product: null,
        product_id: null,
        search: '',
        suggestions: [],
        showDropdown: false,
        quantity: 1,
        notes: '',
        error: null,
        quantityError: null,
        hasError: false
      })
    }

    const removeLine = (index) => {
      items.value.splice(index, 1)
    }

    const confirmRemoveLine = (index) => {
      lineToDelete.value = index
      showDeleteModal.value = true
    }

    const confirmDelete = () => {
      if (lineToDelete.value !== null) {
        removeLine(lineToDelete.value)
        lineToDelete.value = null
      }
      showDeleteModal.value = false
    }

    const searchProducts = (index) => {
      const line = items.value[index]
      if (!line) return
      const q = (line.search || '').trim()
      if (!q) {
        line.suggestions = []
        return
      }
      // Rechercher uniquement les produits qui suivent l'inventaire
      productsService.getProducts({
        search: q,
        per_page: 10,
        track_inventory: true
      }).then(res => {
        const data = res.data
        const allProducts = Array.isArray(data) ? data : (data.data || [])
        // Filtrer pour ne garder que ceux qui suivent l'inventaire
        line.suggestions = allProducts.filter(p => p.track_inventory === true)
      }).catch(() => {
        line.suggestions = []
      }).finally(() => {
        line.showDropdown = true
      })
    }

    const selectProduct = (index, product) => {
      const line = items.value[index]
      if (!line) return

      // Vérifier si le produit suit l'inventaire
      if (!product.track_inventory) {
        toastStore.addToast({
          type: 'error',
          message: 'Ce produit ne suit pas l\'inventaire',
          duration: 3000
        })
        return
      }

      // Vérifier les doublons
      const duplicateIndex = items.value.findIndex((l, i) => l.product_id === product.id && i !== index)
      if (duplicateIndex !== -1) {
        toastStore.addToast({
          type: 'warning',
          message: 'Ce produit est déjà ajouté dans le retrait',
          duration: 3000
        })
        line.showDropdown = false
        line.suggestions = []
        line.search = ''
        return
      }

      line.product = product
      line.product_id = product.id
      line.search = product.name
      line.showDropdown = false
      line.suggestions = []
      line.quantity = 1
      line.error = null
      line.hasError = false
      
      // Valider la quantité après sélection
      validateQuantity(index)
      
      addEmptyLine()
    }

    const validateQuantity = (index) => {
      const line = items.value[index]
      if (!line) return false

      line.quantityError = null
      line.hasError = false

      if (!line.product || !line.product_id) {
        line.error = 'Veuillez sélectionner un produit'
        line.hasError = true
        return false
      }

      const quantity = Number(line.quantity) || 0
      const availableStock = Number(line.product.stock_quantity) || 0

      if (quantity <= 0) {
        line.quantityError = 'La quantité doit être supérieure à 0'
        line.hasError = true
        return false
      }

      if (quantity > availableStock) {
        line.quantityError = `Stock insuffisant. Disponible: ${availableStock}`
        line.hasError = true
        return false
      }

      line.error = null
      line.hasError = false
      return true
    }

    const calculateStockAfter = (line) => {
      if (!line.product || !line.quantity) return 0
      const currentStock = Number(line.product.stock_quantity) || 0
      const quantity = Number(line.quantity) || 0
      return Math.max(0, currentStock - quantity)
    }

    const getStockClass = (stock) => {
      if (stock <= 0) return 'stock-zero'
      if (stock <= 5) return 'stock-low'
      return 'stock-ok'
    }

    const onGlobalSearch = () => {
      if (globalSearchTimer.value) clearTimeout(globalSearchTimer.value)
      const q = (globalSearch.value || '').trim()
      if (!q) {
        globalSuggestions.value = []
        return
      }
      globalSearchTimer.value = setTimeout(() => {
        productsService.getProducts({ 
          search: q, 
          per_page: 10,
          track_inventory: true 
        })
          .then(res => {
            const data = res.data
            const allProducts = Array.isArray(data) ? data : (data.data || [])
            // Filtrer pour ne garder que ceux qui suivent l'inventaire
            globalSuggestions.value = allProducts.filter(p => p.track_inventory === true)
          })
          .catch(() => { globalSuggestions.value = [] })
      }, 300)
    }

    const addLineFromGlobal = () => {
      if (!globalSuggestions.value.length) return
      const product = globalSuggestions.value[0]
      const existingIndex = items.value.findIndex(l => l.product_id === product.id)
      if (existingIndex !== -1) {
        toastStore.addToast({
          type: 'warning',
          message: 'Ce produit est déjà présent dans le retrait',
          duration: 3000
        })
        return
      }
      addEmptyLine()
      selectProduct(items.value.length - 1, product)
      globalSearch.value = ''
      globalSuggestions.value = []
    }

    const validate = () => {
      const e = {}
      if (!form.value.withdrawal_date) e.withdrawal_date = 'La date est requise'
      if (!form.value.reason) e.reason = 'La raison est requise'
      if (items.value.length === 0) {
        toastStore.addToast({ type: 'error', message: 'Ajoutez au moins un article', duration: 3000 })
        return false
      }

      // Valider toutes les lignes
      let hasErrors = false
      for (let i = 0; i < items.value.length; i++) {
        if (!validateQuantity(i)) {
          hasErrors = true
        }
      }

      if (hasErrors) {
        toastStore.addToast({ 
          type: 'error', 
          message: 'Vérifiez les quantités (stock insuffisant ou invalide)', 
          duration: 3500 
        })
        return false
      }

      // Contrôle des doublons
      const productIds = items.value.map(l => l.product_id).filter(Boolean)
      const hasDuplicates = new Set(productIds).size !== productIds.length
      if (hasDuplicates) {
        toastStore.addToast({ 
          type: 'error', 
          message: 'Vous avez des doublons de produits dans le retrait', 
          duration: 3500 
        })
        return false
      }

      errors.value = e
      return Object.keys(e).length === 0 && !hasErrors && !hasDuplicates
    }

    const handleCancel = () => {
      if (items.value.length > 0 || form.value.reason) {
        showCancelModal.value = true
      } else {
        router.push({ name: 'stock-withdrawals' })
      }
    }

    const confirmCancel = () => {
      showCancelModal.value = false
      router.push({ name: 'stock-withdrawals' })
    }

    const handleSubmit = () => {
      if (!validate()) return
      showSubmitModal.value = true
    }

    const confirmSubmit = async () => {
      if (isSubmitting.value) return
      isSubmitting.value = true
      try {
        const payload = {
          withdrawal_date: form.value.withdrawal_date,
          reason: form.value.reason,
          notes: form.value.notes || null,
          items: items.value
            .filter(l => l.product_id && l.quantity > 0)
            .map(l => ({
              product_id: l.product_id,
              quantity: Number(l.quantity),
              notes: l.notes || null
            }))
        }

        await stockWithdrawalService.createStockWithdrawal(payload)
        toastStore.addToast({ 
          type: 'success', 
          message: 'Retrait de stock créé avec succès', 
          duration: 4000 
        })
        showSubmitModal.value = false
        router.push({ name: 'stock-withdrawals' })
      } catch (err) {
        console.error('Erreur création retrait:', err)
        const errorMessage = err?.response?.data?.message || err?.message || 'Erreur lors de la création du retrait'
        toastStore.addToast({
          type: 'error',
          message: errorMessage,
          duration: 4000
        })
      } finally {
        isSubmitting.value = false
      }
    }

    const formatDate = (date) => {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const getReasonLabel = (reason) => {
      return stockWithdrawalService.getReasonLabel(reason) || reason
    }

    // Initialiser avec une ligne vide
    addEmptyLine()

    return {
      form,
      errors,
      isSubmitting,
      items,
      globalSearch,
      globalSuggestions,
      canAddFromGlobal,
      addLineFromGlobal,
      onGlobalSearch,
      addEmptyLine,
      removeLine,
      confirmRemoveLine,
      searchProducts,
      selectProduct,
      validateQuantity,
      calculateStockAfter,
      getStockClass,
      totalQuantity,
      handleCancel,
      confirmCancel,
      handleSubmit,
      confirmSubmit,
      confirmDelete,
      formatDate,
      getReasonLabel,
      showCancelModal,
      showDeleteModal,
      showSubmitModal
    }
  }
})
</script>

<style scoped>
.create-withdrawal-view {
  padding: 0 0 24px 0;
  max-width: 1600px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #0A5C6B;
}

.back-btn svg {
  width: 20px;
  height: 20px;
  stroke: #475569;
  stroke-width: 2;
}

.header-info {
  flex: 1;
}

.page-heading {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
  letter-spacing: -0.5px;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.actions {
  display: flex;
  gap: 8px;
}

.primary-btn,
.secondary-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.primary-btn svg,
.secondary-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.primary-btn {
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(10, 92, 107, 0.2);
}

.primary-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(10, 92, 107, 0.3);
}

.primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.secondary-btn {
  background: white;
  color: #475569;
  border: 1.5px solid #e2e8f0;
}

.secondary-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: 24px;
}

.left-column,
.right-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.progress-indicator {
  display: flex;
  align-items: center;
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
  gap: 12px;
}

.progress-step {
  display: flex;
  align-items: center;
  gap: 12px;
  opacity: 0.4;
  transition: opacity 0.3s;
}

.progress-step.active {
  opacity: 1;
}

.progress-step.completed {
  opacity: 1;
}

.step-number {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  transition: all 0.3s;
  flex-shrink: 0;
}

.progress-step.active .step-number {
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(10, 92, 107, 0.3);
}

.progress-step.completed .step-number {
  background: #e8f5e9;
  color: #2e7d32;
}

.step-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.step-title {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}

.step-desc {
  font-size: 12px;
  color: #64748b;
}

.progress-line {
  flex: 1;
  height: 2px;
  background: #e2e8f0;
  transition: background 0.3s;
}

.progress-line.completed {
  background: linear-gradient(90deg, #0A5C6B 0%, #0e7689 100%);
}

.card {
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  transition: all 0.3s;
}

.card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f1f5f9;
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.card-icon svg {
  width: 24px;
  height: 24px;
  stroke-width: 2;
}

.details-icon {
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
}

.details-icon svg {
  stroke: #7b1fa2;
}

.products-icon {
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
}

.products-icon svg {
  stroke: #7b1fa2;
}

.card-title-group {
  flex: 1;
}

.card-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
  letter-spacing: -0.3px;
}

.card-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 4px;
}

.required {
  color: #dc2626;
  font-weight: 700;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  z-index: 1;
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-with-icon input,
.input-with-icon select {
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  transition: all 0.2s;
  background: white;
}

.input-with-icon input:focus,
.input-with-icon select:focus {
  outline: none;
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.textarea-wrapper {
  position: relative;
}

.textarea-wrapper .input-icon {
  position: absolute;
  left: 12px;
  top: 12px;
}

.textarea-wrapper textarea {
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.2s;
}

.textarea-wrapper textarea:focus {
  outline: none;
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.field-error {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #dc2626;
  font-size: 12px;
  font-weight: 500;
}

.field-error svg {
  width: 14px;
  height: 14px;
  stroke-width: 2;
  flex-shrink: 0;
}

.field-error-inline {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #dc2626;
  font-size: 11px;
  font-weight: 500;
  margin-top: 4px;
}

.field-error-inline.small {
  font-size: 10px;
}

.field-error-inline svg {
  width: 12px;
  height: 12px;
  stroke-width: 2;
  flex-shrink: 0;
}

.search-toolbar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.search-box {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 0 14px;
  transition: all 0.2s;
}

.search-box:focus-within {
  background: white;
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.search-icon {
  width: 18px;
  height: 18px;
  stroke: #94a3b8;
  stroke-width: 2;
  flex-shrink: 0;
}

.search-box input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  padding: 12px 12px;
  font-size: 14px;
  color: #0f172a;
}

.quick-add-badge {
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.add-quick-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.add-quick-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(10, 92, 107, 0.3);
}

.add-quick-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.add-quick-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  gap: 16px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon svg {
  width: 40px;
  height: 40px;
  stroke: #94a3b8;
  stroke-width: 2;
}

.empty-state h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.empty-state p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  max-width: 400px;
}

.add-first-line-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;
}

.add-first-line-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(10, 92, 107, 0.3);
}

.add-first-line-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.table-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.table-wrapper {
  overflow-x: auto;
  overflow-y: visible;
  border-radius: 10px;
  max-height: 400px;
  border: 1.5px solid #e2e8f0;
}

.products-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1000px;
}

.products-table thead {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.products-table th {
  padding: 14px 12px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e2e8f0;
}

.products-table td {
  padding: 12px;
  border-bottom: 1px solid #f1f5f9;
}

.product-row:hover {
  background: #fafbfc;
}

.product-row.has-error {
  background: #fef2f2;
}

.product-row.has-error:hover {
  background: #fee2e2;
}

.product-row:last-child td {
  border-bottom: none;
}

.col-product {
  width: 250px;
}

.col-number {
  width: 150px;
}

.col-notes {
  width: 200px;
}

.col-action {
  width: 60px;
  text-align: center;
}

.product-cell {
  position: relative;
}

.product-search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon-sm {
  position: absolute;
  left: 10px;
  width: 16px;
  height: 16px;
  stroke: #94a3b8;
  stroke-width: 2;
  z-index: 1;
}

.product-input {
  width: 100%;
  padding: 10px 10px 10px 34px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;
}

.product-input:focus {
  outline: none;
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.product-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  max-height: 240px;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 1000;
}

.product-option {
  padding: 12px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.product-option:last-child {
  border-bottom: none;
}

.product-option:hover {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.product-option-content {
  flex: 1;
}

.product-name {
  font-weight: 600;
  color: #0f172a;
  font-size: 14px;
  margin-bottom: 4px;
}

.product-meta {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #64748b;
}

.product-id {
  font-weight: 600;
}

.product-stock {
  font-weight: 600;
  color: #0A5C6B;
}

.number-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.number-input {
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  text-align: right;
  transition: all 0.2s;
}

.number-input.input-error {
  border-color: #dc2626;
}

.number-input:focus {
  outline: none;
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.stock-display,
.stock-after-display {
  text-align: right;
  padding: 8px 0;
}

.stock-value {
  font-size: 14px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  display: inline-block;
}

.stock-value.stock-zero {
  color: #dc2626;
  background: #fee2e2;
}

.stock-value.stock-low {
  color: #d97706;
  background: #fef3c7;
}

.stock-value.stock-ok {
  color: #059669;
  background: #d1fae5;
}

.stock-placeholder {
  color: #94a3b8;
  font-size: 14px;
}

.notes-input {
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  transition: all 0.2s;
}

.notes-input:focus {
  outline: none;
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.delete-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1.5px solid #fee2e2;
  background: #fef2f2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.delete-btn:hover {
  background: #fecaca;
  border-color: #fca5a5;
  transform: scale(1.05);
}

.delete-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.add-line-section {
  padding: 16px 0 0;
  border-top: 2px dashed #e2e8f0;
}

.add-line-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  color: #0A5C6B;
  border: 1.5px dashed #0A5C6B;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.add-line-btn:hover {
  background: #f0f9ff;
  border-style: solid;
}

.add-line-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.sticky-card {
  position: sticky;
  top: 20px;
}

.summary-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 2px solid #e2e8f0;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e2e8f0;
}

.summary-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-icon svg {
  width: 22px;
  height: 22px;
  stroke: white;
  stroke-width: 2;
}

.summary-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: white;
  border-radius: 8px;
  gap: 12px;
}

.summary-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
}

.summary-label svg {
  width: 16px;
  height: 16px;
  stroke-width: 2;
  stroke: #94a3b8;
}

.summary-value {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.summary-value.highlight {
  font-size: 18px;
  color: #0A5C6B;
}

.summary-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
}

.summary-actions {
  padding-top: 16px;
  border-top: 2px solid #e2e8f0;
}

.summary-save-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(10, 92, 107, 0.3);
}

.summary-save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(10, 92, 107, 0.4);
}

.summary-save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.summary-save-btn svg {
  width: 20px;
  height: 20px;
  stroke-width: 2.5;
}

.spinner-small {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideUp 0.3s ease-out;
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  padding: 24px 24px 20px;
  text-align: center;
}

.modal-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.modal-header.warning .modal-icon {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
}

.modal-header.warning .modal-icon svg {
  width: 32px;
  height: 32px;
  stroke: #e65100;
  stroke-width: 2;
}

.modal-header.danger .modal-icon {
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
}

.modal-header.danger .modal-icon svg {
  width: 32px;
  height: 32px;
  stroke: #c62828;
  stroke-width: 2;
}

.modal-header.success .modal-icon {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
}

.modal-header.success .modal-icon svg {
  width: 32px;
  height: 32px;
  stroke: #2e7d32;
  stroke-width: 2;
}

.modal-header h3 {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.modal-body {
  padding: 0 24px 24px;
}

.modal-body p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  text-align: center;
  line-height: 1.6;
}

.modal-summary {
  background: #f8fafc;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.modal-summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.modal-summary-item .label {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
}

.modal-summary-item .value {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.modal-summary-item .value.highlight {
  color: #0A5C6B;
  font-size: 16px;
}

.modal-actions {
  display: flex;
  gap: 12px;
  padding: 0 24px 24px;
}

.modal-btn {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.modal-btn.secondary {
  background: #f1f5f9;
  color: #475569;
}

.modal-btn.secondary:hover {
  background: #e2e8f0;
}

.modal-btn.primary {
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.modal-btn.primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(10, 92, 107, 0.3);
}

.modal-btn.primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-btn.danger {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
}

.modal-btn.danger:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 1280px) {
  .layout {
    grid-template-columns: 1fr 340px;
  }
}

@media (max-width: 1024px) {
  .layout {
    grid-template-columns: 1fr;
  }

  .sticky-card {
    position: static;
  }

  .progress-indicator {
    flex-wrap: wrap;
  }

  .progress-step {
    flex: 1;
    min-width: 140px;
  }

  .progress-line {
    display: none;
  }
}

@media (max-width: 768px) {
  .create-withdrawal-view {
    padding: 0 0 16px 0;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-left {
    flex-direction: column;
    align-items: stretch;
  }

  .back-btn {
    align-self: flex-start;
  }

  .actions {
    width: 100%;
  }

  .actions button {
    flex: 1;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .progress-indicator {
    flex-direction: column;
    gap: 16px;
  }

  .progress-step {
    width: 100%;
  }

  .table-wrapper {
    border-radius: 0;
    margin: 0 -24px;
  }

  .card {
    border-radius: 10px;
    padding: 16px;
  }

  .search-toolbar {
    flex-direction: column;
  }

  .search-box {
    width: 100%;
  }

  .products-table {
    min-width: 900px;
  }
}
.stock-display,
.stock-after-display {
  text-align: right;
  padding: 8px 0;
}

.stock-value {
  font-size: 14px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
}

.stock-value.stock-zero {
  color: #dc2626;
  background: #fee2e2;
}

.stock-value.stock-low {
  color: #d97706;
  background: #fef3c7;
}

.stock-value.stock-ok {
  color: #059669;
  background: #d1fae5;
}

.stock-placeholder {
  color: #94a3b8;
  font-size: 14px;
}

.field-error-inline {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #dc2626;
  font-size: 11px;
  font-weight: 500;
  margin-top: 4px;
}

.field-error-inline.small {
  font-size: 10px;
}

.field-error-inline svg {
  width: 12px;
  height: 12px;
  stroke-width: 2;
  flex-shrink: 0;
}

.input-error {
  border-color: #dc2626 !important;
}

.product-row.has-error {
  background: #fef2f2;
}

.product-row.has-error:hover {
  background: #fee2e2;
}

.col-notes {
  width: 200px;
}

.notes-input {
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  transition: all 0.2s;
}

.notes-input:focus {
  outline: none;
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.details-icon {
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
}

.details-icon svg {
  stroke: #7b1fa2;
}

.product-option .product-stock {
  font-weight: 600;
  color: #0A5C6B;
}
</style>

